import Button from '@atlaskit/button/standard-button';
import DropdownMenu, { type CustomTriggerProps } from '@atlaskit/dropdown-menu';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { type Size } from '@atlaskit/icon/types';
import { token } from '@atlaskit/tokens';
import { usePostOfficeContext } from '@atlassian/post-office-context';
import { useFireAdControlAnalyticsEvents } from '@atlassian/surface-analytics';
import { type TargetType } from '@atlassiansox/cross-flow-support';
import { css } from '@compiled/react';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { useMessageContext } from '@atlassian/post-office-context/message-context';
import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';
import { useMessageEvent } from '@post-office/message-lifecycle';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { AdControlsFlags, useAdControlsFlags } from './components/dismissalFlag';
import { AppSwitcherDropdown } from './components/dropdownMenu';
import { WhyAmISeeingThisModal } from './components/modal';
import { dismissApp } from './services/app-recommendation-service';
import { type JSX } from 'react/jsx-runtime';

type DropdownTriggerProps = CustomTriggerProps<HTMLElement> & {
	onMouseEnter: React.MouseEventHandler<HTMLElement>;
	onMouseLeave: React.MouseEventHandler<HTMLElement>;
	isHovered: boolean;
	name: string;
	iconSize?: Size;
	spacing?: 'default' | 'compact';
};

const dropdownButtonSizeMedium = css({
	minWidth: token('space.400', '32px'),
	height: token('space.400', '32px'),
});

const dropdownButtonSize = css({
	minWidth: token('space.600', '48px'),
	height: token('space.600', '48px'),
});

const dropdownTriggerStyles = css({
	padding: 0,
	marginBlock: 0, // top and bottom
	marginInline: `${token('space.025', '2px')} ${token('space.100', '8px')}`, // left and right
	'&:focus-visible': {
		outlineOffset: 0,
		outline: 'revert',
		outlineColor: token('color.border.focused', '#4C9AFF'),
	},
});

const dropdownTriggerWithHoverStyles = css({
	backgroundColor: token('color.background.neutral.subtle.hovered', 'rgba(9, 30, 66, 0.04)'),
	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered', '#EBECF0'),
	},
	// override a transition that has a short, but noticeable delay
	transition: 'background 0s ease-out 0s',
});

const DropdownTrigger = ({
	triggerRef,
	onMouseEnter,
	onMouseLeave,
	isHovered,
	name,
	iconSize = 'small',
	spacing = 'default',
	isSelected,
	...triggerProps
}: DropdownTriggerProps) => {
	const intl = useIntl();
	const ariaLabel = intl.formatMessage(
		{
			id: 'growth.recommendation-jsw.recommendations.dropdown-menu-trigger.aria-label',
			defaultMessage: '{productName} options',
			description: 'Options for a product in the recommendations list',
		},
		{ productName: name },
	);

	return (
		<Button
			{...triggerProps}
			aria-label={ariaLabel}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			appearance="subtle"
			spacing={spacing}
			ref={triggerRef}
			css={[
				iconSize === 'medium' ? dropdownButtonSizeMedium : dropdownButtonSize,
				dropdownTriggerStyles,
				isHovered && !isSelected && dropdownTriggerWithHoverStyles,
			]}
			iconBefore={<ShowMoreHorizontalIcon label={undefined as unknown as string} />}
		/>
	);
};

/*--recommendation--*/

export type Props = {
	app: {
		hamsIdentifier: TargetType;
		ccpProductId: string;
		appName: string;
		/** Optional name to show in ad controls modal. Defaults to appName if not provided. */
		adControlsName?: string;
	};
	onDismissed: (messageInstanceId: string) => void;
	iconSize?: Size;
	spacing?: 'default' | 'compact';
	additionalAnalyticsAttributes?: Record<string, string | number | boolean>;
};

export const RecommendationControls = ({
	app,
	onDismissed,
	iconSize,
	spacing,
	additionalAnalyticsAttributes = {},
}: Props): JSX.Element => {
	// This is to handle a specific design requirement to hightlight the dropdown on hover
	// Couldn't work out how to do this in css, so its js :(
	const [isHovered, setIsHovered] = useState(false);
	const [isWhyAmISeeingThisModelOpened, setIsWhyAmISeeingThisModelOpened] = useState(false);
	const { messageDismissed, messageAcknowledged } = useMessageEvent();
	const { messageInstanceClicked } = useMessageInstanceEvent();
	const { messageInstanceId } = useMessageContext();
	const context = usePostOfficeContext();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const { fireInteractedUIEvent: fireAdControlInteractedUIEvent } =
		useFireAdControlAnalyticsEvents();
	const { showFlag, onDismissFlag, dismissedApps } = useAdControlsFlags();
	const { isSiteAdmin } = usePostOfficeContext();

	const handleNotInterested = async () => {
		await messageDismissed();
		await messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'notInterested',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: {
				actionType: 'dismiss',
			},
			additionalMetadataFromFrontend: {},
		});
		fireAnalyticsEvent(
			{
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'notInterested',
			},
			{ isSiteAdmin, ...additionalAnalyticsAttributes },
		);
		const success = await dismissApp(app.ccpProductId, context);

		if (messageInstanceId && success) {
			onDismissed(messageInstanceId);
		}
		showFlag(app);
	};

	const handleWhyAmISeeingThis = async () => {
		setIsWhyAmISeeingThisModelOpened(true);
		await messageAcknowledged();
		await messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'adControlsReasons',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: {
				actionType: 'acknowledge',
			},
			additionalMetadataFromFrontend: {},
		});
		fireAnalyticsEvent(
			{
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'adControlsReasons',
			},
			{ ...additionalAnalyticsAttributes },
		);
		fireAnalyticsEvent(
			{
				eventType: 'screen',
				name: 'adControlsReasonsModal',
			},
			{
				...additionalAnalyticsAttributes,
			},
		);
	};

	return (
		<>
			<DropdownMenu
				trigger={({ triggerRef, ...triggerProps }) => (
					<DropdownTrigger
						name={app.appName}
						triggerRef={triggerRef}
						onMouseEnter={() => setIsHovered(true)}
						onMouseLeave={() => setIsHovered(false)}
						isHovered={isHovered}
						iconSize={iconSize}
						spacing={spacing}
						{...triggerProps}
					/>
				)}
				shouldRenderToParent
				onOpenChange={(args) => {
					if (args.isOpen) {
						fireAdControlInteractedUIEvent(
							{
								action: 'clicked',
								actionSubject: 'button',
								actionSubjectId: 'adControlsDropdown',
							},
							{
								source: 'atlassianSwitcherDiscoverProductsSection',
								targetProductId: app.ccpProductId,
							},
						);
						fireAnalyticsEvent({
							eventType: 'ui',
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId: 'adControlsDropdown',
						});
					}
				}}
			>
				<AppSwitcherDropdown
					onNotInterested={handleNotInterested}
					onWhyAmISeeingThis={handleWhyAmISeeingThis}
				/>
			</DropdownMenu>
			<WhyAmISeeingThisModal
				isOpen={isWhyAmISeeingThisModelOpened}
				onClose={() => {
					setIsWhyAmISeeingThisModelOpened(false);
					fireAnalyticsEvent(
						{
							eventType: 'ui',
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId: 'adControlsReasonsClose',
						},
						{ ...additionalAnalyticsAttributes },
					);
				}}
				app={{ ccpProductId: app.ccpProductId, appName: app.adControlsName || app.appName }}
			/>
			{dismissedApps.includes(app) && (
				<AdControlsFlags dismissedApps={[app]} onDismissFlag={() => onDismissFlag(app)} />
			)}
		</>
	);
};
